var App = App || {};

// PromosSelector module with integrated Splide carousel initialization
App.PromosSelector = (function () {
    'use strict';

    // Check if we're in CMS mode
    const inCms = window.parent !== window;

    function getPerPageForViewport(slideCount) {
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

        if (viewportWidth <= 575) {
            return 1;
        }

        if (viewportWidth <= 1199) {
            return Math.min(3, slideCount);
        }

        if (viewportWidth <= 1499) {
            return Math.min(4, slideCount);
        }

        return Math.min(5, slideCount);
    }

    function shouldEnableCarousel(carousel) {
        if (!carousel) return false;

        const slides = Array.from(carousel.querySelectorAll('.splide__slide:not(.splide__slide--clone)'));
        if (slides.length < 2) return false;

        const perPage = getPerPageForViewport(slides.length);
        return slides.length > perPage;
    }

    function setCarouselEnabledState(component, carousel, isEnabled) {
        if (!carousel) return;

        if (component) {
            component.classList.toggle('promos-selector-component--carousel-enabled', isEnabled);
        }

        carousel.classList.toggle('promos-selector-component__carousel--enabled', isEnabled);

        const arrows = carousel.querySelector('.splide__arrows');
        if (arrows) {
            arrows.hidden = !isEnabled;
            arrows.setAttribute('aria-hidden', (!isEnabled).toString());
        }

        if (!isEnabled) {
            // Splide base CSS hides non-initialized roots; force visibility for static fallback mode.
            try {
                carousel.style.visibility = 'visible';
                carousel.classList.add('is-rendered');
            } catch (e) {
                // ignore
            }
        }
    }

    function updateCarouselNavigationState(splide, carousel) {
        if (!splide || !carousel) return;

        const arrows = carousel.querySelector('.splide__arrows');
        const slideCount = splide.length || carousel.querySelectorAll('.splide__slide:not(.splide__slide--clone)').length;

        if (!arrows || slideCount === 0) return;

        const perPage = Number(splide.options && splide.options.perPage) || getPerPageForViewport(slideCount);
        const hasMultiplePages = slideCount > perPage;

        arrows.hidden = !hasMultiplePages;
        arrows.setAttribute('aria-hidden', (!hasMultiplePages).toString());

        if (!hasMultiplePages && splide.index !== 0) {
            splide.go(0);
        }
    }

    function destroySplideCarousel(component, carousel) {
        if (!carousel) return;

        const existing = carousel._promosSelectorSplide;
        if (existing && typeof existing.destroy === 'function') {
            existing.destroy(true);
        }

        carousel._promosSelectorSplide = null;
        carousel.dataset.splideMounted = 'false';
        setCarouselEnabledState(component, carousel, false);

        try {
            carousel.style.visibility = 'visible';
            carousel.classList.add('is-rendered');
        } catch (e) {
            // ignore
        }
    }

    function initSplideCarousel(component, carousel) {
        if (!carousel) return null;
        if (carousel.dataset.splideMounted === 'true' && carousel._promosSelectorSplide) {
            return carousel._promosSelectorSplide;
        }

        // If Splide is not present, unhide the splide root so content shows
        if (typeof Splide === 'undefined') {
            console.warn('PromosSelector: Splide library not found. Ensure splide.min.js is loaded before this script. Falling back to showing content without carousel.');
            setCarouselEnabledState(component, carousel, false);
            try {
                carousel.style.visibility = 'visible';
                carousel.classList.add('is-rendered');
            } catch (e) {
                // ignore
            }
            return null;
        }

        var slideCount = carousel.querySelectorAll('.splide__slide').length;
        if (slideCount < 2) {
            setCarouselEnabledState(component, carousel, false);
            try {
                carousel.style.visibility = 'visible';
                carousel.classList.add('is-rendered');
            } catch (e) { }
            return null;
        }

        var desktopPerPage = Math.min(5, slideCount);

        try {
            const splide = new Splide(carousel, {
                type: "slide",
                arrows: true,
                pagination: false,
                accessibility: !inCms,
                drag: !inCms,
                autoplay: false,
                perPage: desktopPerPage,
                perMove: 1,
                focus: 0,
                trimSpace: 'move',
                omitEnd: true,
                breakpoints: {
                    1499: {
                        perPage: Math.min(4, slideCount),
                        padding: { right: '7rem', left: '0' }
                    },
                    1199: {
                        arrows: true,
                        drag: !inCms,
                        perPage: Math.min(3, slideCount),
                        padding: { right: '0', left: '0' }
                    },
                    993: {
                        perPage: Math.min(3, slideCount),
                    },
                    767: {
                        perPage: Math.min(2, slideCount),
                    },
                    575: {
                        perPage: 1,
                        autoplay: false,
                        padding: {
                            right: '5rem',
                            left: '0'
                        }
                    }
                }
            });

            const syncNavigationState = function () {
                window.requestAnimationFrame(function () {
                    updateCarouselNavigationState(splide, carousel);
                });
            };

            splide.on('mounted moved resized refreshed updated', syncNavigationState);
            splide.mount();
            carousel._promosSelectorSplide = splide;
            syncNavigationState();
            carousel.dataset.splideMounted = 'true';
            setCarouselEnabledState(component, carousel, true);
            return splide;
        } catch (err) {
            console.error('PromosSelector: error initializing Splide', err);
            // Ensure content is visible even if initialization fails
            setCarouselEnabledState(component, carousel, false);
            try {
                carousel.style.visibility = 'visible';
                carousel.classList.add('is-rendered');
            } catch (e) { }
            return null;
        }
    }

    function evaluateCarousel(component, carousel) {
        if (!component || !carousel) return;

        const enableCarousel = shouldEnableCarousel(carousel);
        const isMounted = carousel.dataset.splideMounted === 'true' && !!carousel._promosSelectorSplide;

        if (enableCarousel && !isMounted) {
            initSplideCarousel(component, carousel);
            return;
        }

        if (!enableCarousel && isMounted) {
            destroySplideCarousel(component, carousel);
            return;
        }

        setCarouselEnabledState(component, carousel, enableCarousel);
        if (isMounted && carousel._promosSelectorSplide) {
            carousel._promosSelectorSplide.refresh();
            updateCarouselNavigationState(carousel._promosSelectorSplide, carousel);
        }
    }

    function init() {
        const components = document.querySelectorAll(".promos-selector-component");
        components.forEach(function (component) {
            // The splide element is on the same element as the carousel class, not a child.
            const carousel = component.querySelector(".promos-selector-component__carousel.splide, .promos-selector-component__carousel .splide, .splide");
            if (carousel) {
                evaluateCarousel(component, carousel);

                if (component.dataset.promoCarouselBound !== 'true') {
                    component.dataset.promoCarouselBound = 'true';

                    let resizeTimeout;
                    const handleResize = function () {
                        window.clearTimeout(resizeTimeout);
                        resizeTimeout = window.setTimeout(function () {
                            evaluateCarousel(component, carousel);
                        }, 120);
                    };

                    window.addEventListener('resize', handleResize);
                    window.addEventListener('orientationchange', handleResize);

                    if (typeof ResizeObserver !== 'undefined') {
                        const resizeObserver = new ResizeObserver(function () {
                            handleResize();
                        });
                        resizeObserver.observe(component);
                        component._promoResizeObserver = resizeObserver;
                    }
                }
            }

            // Attach click-on-card behavior for slides that have a data-href
            // Use delegated handlers to allow Splide clones to work
            const list = component.querySelector('.splide__list');
            if (list) {
                if (list.dataset.promoHandlersBound === 'true') {
                    return;
                }
                list.dataset.promoHandlersBound = 'true';

                let startX = 0;
                let startY = 0;

                list.addEventListener('touchstart', function (e) {
                    if (e.target.closest('.promos-selector-component__item--link')) {
                        const touch = e.touches ? e.touches[0] : e;
                        startX = touch.clientX;
                        startY = touch.clientY;
                    }
                });

                list.addEventListener('mousedown', function (e) {
                    if (e.target.closest('.promos-selector-component__item--link')) {
                        startX = e.clientX;
                        startY = e.clientY;
                    }
                });

                list.addEventListener('touchend', function (e) {
                    const item = e.target.closest('.promos-selector-component__item--link');
                    if (item) {
                        const touch = e.changedTouches ? e.changedTouches[0] : e;
                        const dx = Math.abs(touch.clientX - startX);
                        const dy = Math.abs(touch.clientY - startY);

                        // if movement is small, treat as a click. Threshold 8px
                        if (dx < 8 && dy < 8) {
                            // avoid clicks on interactive children
                            const interactive = e.target.closest('a, button, .promos-selector-component__button');
                            if (interactive) return;

                            const href = item.getAttribute('data-href');
                            const target = item.getAttribute('data-target');
                            if (href) {
                                if (target === '_blank') {
                                    window.open(href, '_blank');
                                } else {
                                    window.location.href = href;
                                }
                            }
                        }
                    }
                });

                list.addEventListener('mouseup', function (e) {
                    const item = e.target.closest('.promos-selector-component__item--link');
                    if (item) {
                        const dx = Math.abs(e.clientX - startX);
                        const dy = Math.abs(e.clientY - startY);

                        // if movement is small, treat as a click. Threshold 8px
                        if (dx < 8 && dy < 8) {
                            // avoid clicks on interactive children
                            const interactive = e.target.closest('a, button, .promos-selector-component__button');
                            if (interactive) return;

                            const href = item.getAttribute('data-href');
                            const target = item.getAttribute('data-target');
                            if (href) {
                                if (target === '_blank') {
                                    window.open(href, '_blank');
                                } else {
                                    window.location.href = href;
                                }
                            }
                        }
                    }
                });
            }
        });
    }

    return {
        init: init
    };

})();

document.addEventListener('DOMContentLoaded', function () {
    App.PromosSelector.init();
});
